// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import type { State } from "@store/reducers";
import { Button, Text, View } from "@components";
import { AbsenceReviewForm } from "@modules/shared/components";
import actionCreator from "@modules/reporting/actionCreator";
import type { SelectedTask } from "@modules/reporting/reducer/tasks/types";
import { formatDateTime } from "@transformers/date";

type OwnProps = $Exact<{
  toggleTasksSection: () => void,
  isTasksHidden: boolean,
  selectedTask: SelectedTask,
  clientId: number,
  employeeId: number,
}>;

type PropsFromDispatch = $Exact<{
  submitAbsenceTask: typeof actionCreator.SubmitAbsenceTask,
  saveDraftAbsenceTask: typeof actionCreator.SaveDraftAbsenceTask,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
  ...PropsFromDispatch,
}>;

const HorizontalRows = ({ data }) => {
  return (
    <View>
      {data.map((el, inx) => {
        const cls = inx !== 0 ? "border-top-color-green" : "";
        return (
          <View key={el.label} direction="row" gap={3}>
            <View flex={1} padding="0.5rem 0" className={cls}>
              <Text className=" font-weight-medium">{el.label}</Text>
            </View>
            <View flex={1} justifyContent="center" className={cls}>
              <Text color="charcoals" {...(el.valueOptions || {})}>
                {el.value}
              </Text>
            </View>
          </View>
        );
      })}
    </View>
  );
};

const AbsenceTaskDetailsSection = (props: Props) => {
  const { t, selectedTask } = props;

  const handleSubmit = (formData: *) => {
    props.submitAbsenceTask({
      clientId: props.clientId,
      employeeId: props.employeeId,
      taskId: props.selectedTask.id,
      task: formData,
    });
  };

  const handleSaveDraft = (formData: *) => {
    props.saveDraftAbsenceTask({
      clientId: props.clientId,
      employeeId: props.employeeId,
      taskId: props.selectedTask.id,
      task: formData,
    });
  };

  const getEmployeeDetailsData = () => {
    return [
      {
        label: t("EMPLOYEE_NAME"),
        value: selectedTask.employee.name,
      },
      {
        label: t("REPORT_COLUMN_EMPLOYEE"),
        value: selectedTask.employee.employeeRefPrimary,
      },
      {
        label: t("DEPARTMENT"),
        value: selectedTask.organisationalUnits[0]?.name || t("N/A"),
      },
    ];
  };

  const getAlertDetailsData = () => {
    return [
      {
        label: t("ABSENCE_START_DATE"),
        value: formatDateTime(selectedTask.absenceStart),
      },
      {
        label: t("ABSENCE_REASON"),
        value: selectedTask.absenceReason.label,
      },
      {
        label: t("ALERT_TRIGGERED"),
        value: selectedTask.alertTriggered?.label,
      },
    ];
  };

  const getCurrentLevelData = () => {
    return [
      {
        label: t("REPORT_COLUMN_ABSENCE_MONITORING_LEVEL"),
        value: selectedTask.currentLevel
          ? `${selectedTask.currentLevel.name} - ${selectedTask.currentLevel.description}`
          : "",
        valueOptions: {
          weight: "bold",
        },
      },
    ];
  };

  return (
    <View>
      <View gap={4} margin="0 0 1rem 0">
        <View className="gs-container" gap={6}>
          <Text type="h6">{t("EMPLOYEE_DETAILS")}</Text>
          <HorizontalRows data={getEmployeeDetailsData()} />
        </View>
        <View className="gs-container" gap={6}>
          <Text type="h6">{t("ALERT_DETAILS")}</Text>
          <HorizontalRows data={getAlertDetailsData()} />
        </View>
        <View className="gs-container" gap={6}>
          <Text type="h6">{t("CURRENT_STATUS")}</Text>
          <HorizontalRows data={getCurrentLevelData()} />
        </View>
      </View>
      <View className="gs-container" gap={6}>
        <Text type="h6">{t("ABSENCE_MONITORING_REVIEW")}</Text>
        <AbsenceReviewForm
          task={props.selectedTask}
          disabled={props.selectedTask ? props.selectedTask.status === "COMPLETED" : false}
          onSubmit={handleSubmit}
          onSaveDraft={handleSaveDraft}
        />
      </View>
    </View>
  );
};

const mapDispatchToProps: PropsFromDispatch = {
  submitAbsenceTask: actionCreator.SubmitAbsenceTask,
  saveDraftAbsenceTask: actionCreator.SaveDraftAbsenceTask,
};

export default compose(localize(), connect(null, mapDispatchToProps))(AbsenceTaskDetailsSection);
